import type {
  CyclePhase,
  ExperienceContext,
  ExperienceScenario,
} from "./experience.types";

function phaseScenario(phase: CyclePhase): ExperienceScenario | null {
  switch (phase) {
    case "protocol":
    case "stimulation":
      return "active_ovarian_stimulation";
    case "trigger":
    case "trigger_shot":
      return "active_trigger";
    case "egg_retrieval":
      return "active_retrieval";
    case "sperm_collection":
      return "active_sperm_collection";
    case "insemination":
      return "active_insemination";
    case "fertilization":
      return "active_fertilization";
    case "embryo_development":
      return "active_embryo_development";
    case "transfer":
      return "active_transfer";
    case "luteal_phase":
      return "waiting_beta";
    default:
      return null;
  }
}

function activeScenario(ctx: ExperienceContext): ExperienceScenario {
  const phase = phaseScenario(ctx.treatment.currentPhase);
  if (phase) return phase;

  if (ctx.treatment.cycleType === "sperm_freezing") {
    return "active_sperm_collection";
  }
  if (ctx.treatment.cycleType === "iui") {
    return "active_insemination";
  }
  if (
    ctx.treatment.cycleType === "ivf" ||
    ctx.treatment.cycleType === "icsi" ||
    ctx.treatment.cycleType === "egg_freezing" ||
    ctx.treatment.cycleType === "embryo_freezing"
  ) {
    return "active_ovarian_stimulation";
  }

  if (ctx.bodyContexts.includes("sperm-production")) {
    return "active_sperm_collection";
  }
  return "active_ovarian_stimulation";
}

function resultScenario(ctx: ExperienceContext): ExperienceScenario {
  switch (ctx.lifecycle.outcome) {
    case "positive":
      return "results_positive";
    case "negative":
      return "results_negative";
    case "canceled":
      return "results_cancelled";
    case "converted":
      return "results_converted";
    case "uncertain":
      return "results_uncertain";
    case "adverse":
      return "results_adverse";
    case "missing":
    case "none":
      return "results_missing";
    default:
      return "results_missing";
  }
}

function helperScenario(ctx: ExperienceContext): ExperienceScenario | null {
  if (ctx.viewer.role === "donor") {
    if (ctx.lifecycle.stage === "active-care") return "donor_active_cycle";
    if (
      ctx.lifecycle.journeyStage === "screening" ||
      ctx.lifecycle.journeyStage === "approved"
    ) {
      return "donor_screening";
    }
    return "donor_application";
  }

  if (ctx.viewer.role === "carrier") {
    if (
      ctx.lifecycle.journeyStage === "matched" ||
      ctx.processContexts.includes("legal")
    ) {
      return "carrier_matched_legal";
    }
    if (ctx.lifecycle.journeyStage === "screening") {
      return "carrier_screening";
    }
    return "carrier_application";
  }

  if (
    ctx.viewer.role === "intended-parent" &&
    (ctx.familyBuildingPlan.treatmentGoal === "surrogacy" ||
      ctx.familyBuildingPlan.partnerChoice === "with-surrogate")
  ) {
    return "intended_parent_matching";
  }

  return null;
}

function planningScenario(ctx: ExperienceContext): ExperienceScenario {
  const { primaryIntent, treatmentGoal, modality } = ctx.familyBuildingPlan;

  if (primaryIntent === "unknown") return "setup_unknown_intent";
  if (primaryIntent === "learn" || treatmentGoal === "exploring") {
    return "learn_explorer";
  }

  // A natural-trying stage is an explicit non-treatment identity. It must be
  // resolved before modality-based planning so an absent modality cannot
  // inherit the historical IVF fallback.
  if (
    ctx.lifecycle.journeyStage === "trying-naturally" &&
    treatmentGoal === "pregnancy"
  ) {
    return "trying_naturally";
  }

  if (treatmentGoal === "surrogacy") return "intended_parent_matching";

  if (
    treatmentGoal === "embryo-freezing" ||
    treatmentGoal === "embryo-preserve"
  ) {
    return "planning_embryo_creation";
  }

  if (
    treatmentGoal === "egg-freezing" ||
    treatmentGoal === "egg-preserve" ||
    (primaryIntent === "preserve" && ctx.bodyContexts.includes("ovarian"))
  ) {
    return "planning_ovarian_preservation";
  }

  if (
    treatmentGoal === "sperm-freezing" ||
    treatmentGoal === "sperm-preserve" ||
    (primaryIntent === "preserve" &&
      ctx.bodyContexts.includes("sperm-production"))
  ) {
    return "planning_sperm_preservation";
  }

  if (treatmentGoal === "iui" || modality === "iui") return "planning_iui";
  if (treatmentGoal === "ivf" || modality === "ivf" || modality === "icsi") {
    return "planning_ivf";
  }

  return "grow_generic";
}

export function resolveExperienceScenario(
  ctx: ExperienceContext,
): ExperienceScenario {
  const helper = helperScenario(ctx);
  if (helper) return helper;

  if (ctx.lifecycle.stage === "setup") return "setup_unknown_intent";
  if (ctx.lifecycle.stage === "active-care") return activeScenario(ctx);

  if (ctx.lifecycle.stage === "waiting") {
    if (
      ctx.familyBuildingPlan.treatmentGoal === "sperm-freezing" ||
      ctx.treatment.cycleType === "sperm_freezing"
    ) {
      return "waiting_semen_results";
    }
    return "waiting_beta";
  }

  if (ctx.lifecycle.stage === "results") return resultScenario(ctx);
  if (
    ctx.lifecycle.stage === "storage" ||
    ctx.lifecycle.stage === "post-cycle"
  ) {
    if (
      ctx.materialContexts.includes("stored-specimens") ||
      ctx.familyBuildingPlan.primaryIntent === "preserve"
    ) {
      return "postcycle_storage";
    }
    return resultScenario(ctx);
  }

  return planningScenario(ctx);
}
